import { useRateManagement } from '@api/useFreight/useFreight'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Form, Input, Select } from 'antd'
import React, { useMemo } from 'react'
import { stateAbbreviations } from '@constants/stateAbbreviations'
import type { AddressField } from '../types'
import type { InvoiceConfigurationComponentProps as BillingAddressConfigurationProps } from '../../../../types'

export function BillingAddressConfiguration({ ratebook, form }: BillingAddressConfigurationProps) {
  const addressFields = useMemo(() => {
    return [
      { key: 'address', label: 'Address', fullWidth: true },
      { key: 'city', label: 'City', fullWidth: true },
      { key: 'state', label: 'State', fullWidth: false, isStateSelect: true },
      { key: 'postal_code', label: 'Postal Code', fullWidth: false },
    ] as AddressField[]
  }, [])

  return (
    <Vertical style={{ maxWidth: 600 }}>
      <Vertical border='top' className='py-3'>
        <Texto category='h5' weight='600' className='mt-3'>
          Billing Address
        </Texto>
      </Vertical>

      <Vertical bg='elevated' border='all' borderRadius='4px' className='py-1 px-3'>
        <Form.Item name='address_info' noStyle>
          <Vertical className='px-2 pt-3' gap={12}>
            {addressFields.map((field: AddressField, index: number) => {
              // For fields that should be rendered on the same row
              if (!field.fullWidth && index < addressFields.length - 1 && !addressFields[index + 1].fullWidth) {
                // Render this field and the next one on the same row
                const nextField = addressFields[index + 1]

                // Skip the next field since we're rendering it now
                addressFields[index + 1] = {
                  ...addressFields[index + 1],
                  rendered: true,
                }

                return !field.rendered ? (
                  <Horizontal
                    key={field.key}
                    gap={16}
                    verticalCenter
                    className={index === addressFields.length - 2 ? 'mb-2' : ''}
                  >
                    <Texto style={{ width: 120 }}>{field.label}:</Texto>
                    {field.isStateSelect ? (
                      <Form.Item noStyle shouldUpdate>
                        {() => (
                          <Select
                            value={form.getFieldValue(['address_info', field.key])}
                            onChange={(value) => form.setFieldValue(['address_info', field.key], value)}
                            placeholder='Select state'
                            style={{ width: 100 }}
                            showSearch
                            filterOption={(input, option) =>
                              typeof option?.value === 'string' &&
                              option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {stateAbbreviations.map((state) => (
                              <Select.Option key={state} value={state}>
                                {state}
                              </Select.Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    ) : (
                      <Form.Item name={['address_info', field.key]} noStyle>
                        <Input style={{ width: 100 }} placeholder={field.label} />
                      </Form.Item>
                    )}

                    <Texto style={{ marginLeft: 16 }}>{nextField.label}:</Texto>
                    {nextField.isStateSelect ? (
                      <Form.Item noStyle shouldUpdate>
                        {() => (
                          <Select
                            value={form.getFieldValue(['address_info', nextField.key])}
                            onChange={(value) => form.setFieldValue(['address_info', nextField.key], value)}
                            placeholder='Select state'
                            style={{ width: 100 }}
                            showSearch
                            filterOption={(input, option) =>
                              typeof option?.value === 'string' &&
                              option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {stateAbbreviations.map((state) => (
                              <Select.Option key={state} value={state}>
                                {state}
                              </Select.Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    ) : (
                      <Form.Item name={['address_info', nextField.key]} noStyle>
                        <Input style={{ width: 120 }} placeholder={nextField.label} />
                      </Form.Item>
                    )}
                  </Horizontal>
                ) : null
              }

              // For full-width fields or if this field should be rendered individually
              return !field.rendered ? (
                <Horizontal
                  key={field.key}
                  gap={16}
                  verticalCenter
                  className={index === addressFields.length - 1 ? 'mb-2' : ''}
                >
                  <Texto style={{ width: 120 }}>{field.label}:</Texto>
                  {field.isStateSelect ? (
                    <Form.Item noStyle shouldUpdate>
                      {() => (
                        <Select
                          value={form.getFieldValue(['address_info', field.key])}
                          onChange={(value) => form.setFieldValue(['address_info', field.key], value)}
                          placeholder='Select state'
                          style={{ width: 100 }}
                          showSearch
                          filterOption={(input, option) =>
                            typeof option?.value === 'string' &&
                            option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {stateAbbreviations.map((state) => (
                            <Select.Option key={state} value={state}>
                              {state}
                            </Select.Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  ) : (
                    <Form.Item name={['address_info', field.key]} noStyle>
                      <Input style={{ flex: 1 }} placeholder={field.label} />
                    </Form.Item>
                  )}
                </Horizontal>
              ) : null
            })}
          </Vertical>
        </Form.Item>
      </Vertical>
    </Vertical>
  )
}
